// SABRINA Service - KBA Asynchroner Nachrichtenaustausch
import { apiClient } from './api';
import {
  SabrinaPosteingangResult,
  SabrinaAbrufResult,
  SabrinaNachricht,
  SabrinaNachrichtPage,
  SabrinaPosteingang,
  SabrinaEmpfangeneNachricht,
  Gks02Gebuehrenbescheid,
  Gks02AbrufResult,
  DatevDispatchRequest,
  DatevDispatch,
  DatevSummary,
  DatevAttempt
} from '../types/sabrina';

const BASE_URL = '/sabrina';

/**
 * Service für SABRINA-Kommunikation mit dem KBA
 * 
 * Hauptfunktionen:
 * - Posteingang prüfen (0006 → 0007): Prüft ob neue Nachrichten verfügbar sind
 * - Nachrichten abrufen (0008 → 0001): Ruft konkrete Nachrichten ab
 * 
 * Wichtig: Polling maximal 1x pro Minute erlaubt!
 */
export const SabrinaService = {
  /**
   * Prüft den Posteingang bei SABRINA (Nachrichtentyp 0006 → 0007)
   * 
   * ACHTUNG: Maximal 1x pro Minute erlaubt!
   * Bei Überschreitung wird HTTP 429 zurückgegeben.
   * 
   * @returns Liste der verfügbaren Nachrichten
   */
  checkPosteingang: async (): Promise<SabrinaPosteingangResult> => {
    const response = await apiClient.get<SabrinaPosteingangResult>(`${BASE_URL}/posteingang`);
    return response.data;
  },

  /**
   * Ruft mehrere Nachrichten vom KBA ab (Nachrichtentyp 0008 → 0001)
   * 
   * @param nachrichtenIds Array der abzurufenden Nachrichten-IDs
   * @returns Ergebnis mit abgerufenen Nachrichten und ggf. Fehlern
   */
  abrufNachrichten: async (nachrichtenIds: string[]): Promise<SabrinaAbrufResult> => {
    const response = await apiClient.post<SabrinaAbrufResult>(`${BASE_URL}/abruf`, { nachrichtenIds });
    return response.data;
  },

  /**
   * Ruft eine einzelne Nachricht vom KBA ab
   * 
   * @param nachrichtenId ID der abzurufenden Nachricht
   * @returns Ergebnis mit der abgerufenen Nachricht
   */
  abrufEinzelneNachricht: async (nachrichtenId: string): Promise<SabrinaAbrufResult> => {
    const response = await apiClient.get<SabrinaAbrufResult>(`${BASE_URL}/abruf/${nachrichtenId}`);
    return response.data;
  },

  /**
   * Lädt alle gespeicherten SABRINA-Nachrichten aus der Datenbank
   * 
   * @param page Seitennummer (0-basiert)
   * @param size Anzahl pro Seite
   * @returns Paginierte Liste der Nachrichten
   */
  getAlleNachrichten: async (page: number = 0, size: number = 20): Promise<SabrinaNachrichtPage> => {
    const response = await apiClient.get<SabrinaNachrichtPage>(`${BASE_URL}/nachrichten`, {
      params: { page, size }
    });
    return response.data;
  },

  /**
   * Lädt alle Nachrichten ohne Pagination (für einfache Listen)
   * 
   * @returns Array aller Nachrichten
   */
  getAlleNachrichtenListe: async (): Promise<SabrinaNachricht[]> => {
    const response = await apiClient.get<SabrinaNachricht[]>(`${BASE_URL}/nachrichten`);
    // Falls Backend Page zurückgibt, content extrahieren
    if (response.data && 'content' in response.data) {
      return (response.data as unknown as SabrinaNachrichtPage).content;
    }
    return response.data;
  },

  /**
   * Lädt Details einer gespeicherten Nachricht inkl. XML-Inhalt
   * 
   * @param id Datenbank-ID der Nachricht
   * @returns Nachricht mit vollständigem XML-Inhalt
   */
  getNachrichtDetails: async (id: number): Promise<SabrinaNachricht> => {
    const response = await apiClient.get<SabrinaNachricht>(`${BASE_URL}/nachrichten/${id}`);
    return response.data;
  },

  /**
   * Lädt alle Nachrichten zu einem bestimmten Zulassungsantrag
   * 
   * @param zulassungsantragId ID des Zulassungsantrags
   * @returns Array der verknüpften Nachrichten
   */
  getNachrichtenByZulassungsantrag: async (zulassungsantragId: number): Promise<SabrinaNachricht[]> => {
    const response = await apiClient.get<SabrinaNachricht[]>(
      `${BASE_URL}/nachrichten/zulassungsantrag/${zulassungsantragId}`
    );
    return response.data;
  },

  // ========== Neue Endpunkte (Backend-Anpassung) ==========

  /**
   * Lädt die gespeicherte Posteingang-Liste aus der DB (ohne KBA-Call).
   * Kann beliebig oft aufgerufen werden.
   */
  getPosteingangListe: async (): Promise<SabrinaPosteingang[]> => {
    const response = await apiClient.get<SabrinaPosteingang[]>(`${BASE_URL}/posteingang/liste`);
    return response.data;
  },

  /**
   * Lädt alle empfangenen und verarbeiteten Nachrichten.
   */
  getEmpfangeneNachrichten: async (): Promise<SabrinaEmpfangeneNachricht[]> => {
    const response = await apiClient.get<SabrinaEmpfangeneNachricht[]>(`${BASE_URL}/empfangen`);
    return response.data;
  },

  /**
   * Lädt eine einzelne empfangene Nachricht mit Details und Dokumenten.
   */
  getEmpfangeneNachricht: async (id: number): Promise<SabrinaEmpfangeneNachricht> => {
    const response = await apiClient.get<SabrinaEmpfangeneNachricht>(`${BASE_URL}/empfangen/${id}`);
    return response.data;
  },

  /**
   * Lädt alle empfangenen Nachrichten für einen bestimmten Zulassungsantrag (serverseitig gefiltert).
   */
  getEmpfangeneNachrichtenByAntragId: async (antragId: number): Promise<SabrinaEmpfangeneNachricht[]> => {
    const response = await apiClient.get<SabrinaEmpfangeneNachricht[]>(`${BASE_URL}/empfangen/by-antrag/${antragId}`);
    return response.data;
  },

  /**
   * Lädt ein SABRINA-Dokument als Blob herunter (mit Bearer-Auth-Header).
   */
  downloadDokument: async (dokumentId: number): Promise<void> => {
    const token = localStorage.getItem('gks_auth_token');
    const baseUrl = apiClient.defaults.baseURL || '/api';
    const url = `${baseUrl}/sabrina/dokumente/${dokumentId}/download`;
    const response = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    if (!response.ok) {
      throw new Error(`Download fehlgeschlagen: ${response.status}`);
    }
    const blob = await response.blob();
    const objectUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    const disposition = response.headers.get('Content-Disposition');
    a.download = disposition?.split('filename="')[1]?.replace('"', '') || 'dokument.pdf';
    a.click();
    window.URL.revokeObjectURL(objectUrl);
  },

  /**
   * Lädt das Request-XML einer gespeicherten Kommunikationsnachricht on-demand.
   */
  getNachrichtRequestXml: async (id: number): Promise<string> => {
    const response = await apiClient.get<string>(`${BASE_URL}/nachrichten/${id}/request-xml`, {
      responseType: 'text',
      headers: { Accept: 'text/plain, text/xml, */*' }
    });
    return response.data;
  },

  /**
   * Lädt das Response-XML einer gespeicherten Kommunikationsnachricht on-demand.
   */
  getNachrichtResponseXml: async (id: number): Promise<string> => {
    const response = await apiClient.get<string>(`${BASE_URL}/nachrichten/${id}/response-xml`, {
      responseType: 'text',
      headers: { Accept: 'text/plain, text/xml, */*' }
    });
    return response.data;
  },

  /**
   * Lädt das Response-XML einer empfangenen Nachricht on-demand.
   */
  getEmpfangeneNachrichtResponseXml: async (id: number): Promise<string> => {
    const response = await apiClient.get<string>(`${BASE_URL}/empfangen/${id}/response-xml`, {
      responseType: 'text',
      headers: { Accept: 'text/plain, text/xml, */*' }
    });
    return response.data;
  },

  /**
   * Exportiert Kommunikationsnachrichten als CSV (clientseitig).
   */
  exportKommunikationCsv: (data: SabrinaNachricht[], filename: string = 'sabrina_kommunikation.csv'): void => {
    const headers = [
      'ID',
      'Nachrichten-ID',
      'Typ',
      'Status',
      'Antrag-ID',
      'Fehlercode',
      'Fehlermeldung',
      'Erstellt am',
      'Aktualisiert am'
    ];

    const escapeCSV = (value: string | number | null | undefined): string => {
      if (value === null || value === undefined) return '';
      const str = String(value);
      if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes(';')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const formatDateTime = (dateString: string | null | undefined): string => {
      if (!dateString) return '';
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const seconds = date.getSeconds().toString().padStart(2, '0');
      return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;
    };

    const rows = data.map(entry => [
      entry.id,
      escapeCSV(entry.nachrichtenId),
      escapeCSV(entry.nachrichtenTyp as string),
      escapeCSV(entry.status),
      entry.zulassungsantragId || '',
      escapeCSV(entry.fehlerCode),
      escapeCSV(entry.fehlerMeldung),
      formatDateTime(entry.erstelltAm),
      formatDateTime(entry.aktualisiertAm)
    ].join(','));

    const csvContent = [headers.join(','), ...rows].join('\n');
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  }
};

export default SabrinaService;

// ============================================================
// GKS02 – Gebührenabrechnung (XKfz 0103)
// ============================================================

const GKS02_BASE = '/sabrina/gks02';

export const Gks02Service = {
  createDatevDispatch: async (request: DatevDispatchRequest): Promise<DatevDispatch> =>
    (await apiClient.post<DatevDispatch>(`${GKS02_BASE}/datev-versand`, request)).data,

  getDatevDispatch: async (id: string): Promise<DatevDispatch> =>
    (await apiClient.get<DatevDispatch>(`${GKS02_BASE}/datev-versand/${id}`)).data,

  getDatevSummaries: async (bescheidIds: number[]): Promise<DatevSummary[]> => {
    const results: DatevSummary[] = [];
    for (let i = 0; i < bescheidIds.length; i += 5000) {
      const response = await apiClient.post<DatevSummary[]>(`${GKS02_BASE}/datev-versand/status`, {
        bescheidIds: bescheidIds.slice(i, i + 5000),
      });
      results.push(...response.data);
    }
    return results;
  },

  getDatevHistory: async (id: number): Promise<DatevAttempt[]> =>
    (await apiClient.get<DatevAttempt[]>(`${GKS02_BASE}/bescheide/${id}/datev-versand`)).data,

  checkPosteingang: async (): Promise<SabrinaPosteingangResult> => {
    const response = await apiClient.get<SabrinaPosteingangResult>(`${GKS02_BASE}/posteingang`);
    return response.data;
  },

  abrufNachrichten: async (nachrichtenIds: string[]): Promise<Gks02AbrufResult> => {
    const response = await apiClient.post<Gks02AbrufResult>(`${GKS02_BASE}/abruf`, { nachrichtenIds });
    return response.data;
  },

  abrufEinzelneNachricht: async (nachrichtenId: string): Promise<Gks02AbrufResult> => {
    const response = await apiClient.get<Gks02AbrufResult>(`${GKS02_BASE}/abruf/${nachrichtenId}`);
    return response.data;
  },

  getAlleBescheide: async (includeBezahlt: boolean = false): Promise<Gks02Gebuehrenbescheid[]> => {
    const response = await apiClient.get<Gks02Gebuehrenbescheid[]>(`${GKS02_BASE}/bescheide`, {
      params: { includeBezahlt }
    });
    return response.data;
  },

  markiereAlsBezahlt: async (id: number): Promise<Gks02Gebuehrenbescheid> => {
    const response = await apiClient.patch<Gks02Gebuehrenbescheid>(`${GKS02_BASE}/bescheide/${id}/bezahlen`);
    return response.data;
  },

  /** Setzt nur die Zahlungsmarkierung zurueck; Versandhistorie bleibt serverseitig erhalten. */
  markiereAlsOffen: async (id: number): Promise<Gks02Gebuehrenbescheid> => {
    const response = await apiClient.patch<Gks02Gebuehrenbescheid>(`${GKS02_BASE}/bescheide/${id}/offen`);
    return response.data;
  },

  getBescheidById: async (id: number): Promise<Gks02Gebuehrenbescheid> => {
    const response = await apiClient.get<Gks02Gebuehrenbescheid>(`${GKS02_BASE}/bescheide/${id}`);
    return response.data;
  },

  getBescheidXml: async (id: number): Promise<string> => {
    const response = await apiClient.get<string>(`${GKS02_BASE}/bescheide/${id}/xml`, {
      responseType: 'text',
      headers: { Accept: 'text/plain, text/xml, */*' }
    });
    return response.data;
  },

  downloadDokument: async (dokumentId: number): Promise<void> => {
    const token = localStorage.getItem('gks_auth_token');
    const baseUrl = apiClient.defaults.baseURL || '/api';
    const url = `${baseUrl}/sabrina/gks02/dokumente/${dokumentId}/download`;
    const response = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    if (!response.ok) throw new Error(`Download fehlgeschlagen: ${response.status}`);
    const blob = await response.blob();
    const objectUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    const disposition = response.headers.get('Content-Disposition');
    a.download = disposition?.split('filename="')[1]?.replace('"', '') || 'gebuehrenbescheid.pdf';
    a.click();
    window.URL.revokeObjectURL(objectUrl);
  }
};
